<!--
  The attention set with its manual controls, shared by the PR header
  (`layout="inline"`) and the Overview's Attention card (`layout="list"`).
  Each name opens a menu (remove them; make it only their turn; undo a
  manual change); "+ Add" and "Set turn…" open a searchable picker of the
  PR's participants. Changes are attention overrides (see PrStore.changeAttention).
-->
<script lang="ts">
  import { onMount, tick } from 'svelte';
  import { subject, type PrStore } from '../state/pr.svelte';
  import { session } from '../state/session.svelte';
  import PendingTag from './PendingTag.svelte';
  import { addChanges, filterParticipants, onlyChanges, reasonText, removeChanges, sameLogin, type Participant } from '../logic/attention';
  import { longestWait, waitText } from '../logic/wait';

  interface Props {
    pr: PrStore;
    layout?: 'inline' | 'list';
  }
  let { pr, layout = 'inline' }: Props = $props();

  const me = $derived(session.user?.login ?? pr.viewer);

  /** The clock waits are read against, ticking each minute while shown. */
  let now = $state(Date.now());
  onMount(() => {
    const timer = setInterval(() => (now = Date.now()), 60_000);
    return () => clearInterval(timer);
  });
  /** When a turn began, as the tooltip says it. */
  const sinceTitle = (since: string | undefined, bound = false) =>
    since
      ? bound
        ? `Waiting since ${new Date(since).toLocaleString()} at the latest: the turn may have begun earlier than the part of the timeline Changeling read`
        : `Waiting since ${new Date(since).toLocaleString()}`
      : undefined;
  /** The header's summary: how long the one waited on (or the longest waiting) has been. */
  const longest = $derived(longestWait(pr.attention, now));
  const longestSince = $derived(longest ? pr.attention.find((m) => m.login === longest.login)?.since : undefined);
  /** The header's summary, "for 2d" or "longest: mara, 2d" ("longest known" when someone's start is unknown). */
  const longestText = $derived(
    !longest ? '' : longest.of === 1 ? `for ${longest.text}` : `${longest.known ? 'longest' : 'longest known'}: ${longest.login}, ${longest.text}`,
  );
  /** An attention change in flight: shown already, and said so until the server confirms it. */
  const saving = $derived(pr.writes.on(subject.attention));
  const inSet = (login: string) => pr.attention.some((m) => sameLogin(m.login, login));
  const overrideOf = (login: string) => pr.overrides.find((o) => sameLogin(o.login, login)) ?? null;
  const NO_RIGHTS = "Changing other people's attention needs triage access to the repository or authorship of the pull request";

  type Open = { kind: 'member'; login: string } | { kind: 'add' } | { kind: 'turn' };
  let open: Open | null = $state(null);
  let trigger: HTMLElement | null = null;
  let pop: HTMLElement | null = $state(null);
  let pos = $state({ top: 0, left: 0 });
  let query = $state('');
  const uid = Math.random().toString(36).slice(2, 8);

  /** Menu actions for a member, by what the viewer may change. */
  function actions(login: string): { label: string; run: () => void }[] {
    const out: { label: string; run: () => void }[] = [];
    if (pr.canSetAttention(login)) out.push({ label: 'Remove from attention', run: () => void pr.changeAttention(removeChanges(login)) });
    const others = pr.attention.filter((m) => !sameLogin(m.login, login));
    if (others.length && others.every((m) => pr.canSetAttention(m.login)) && pr.canSetAttention(login))
      out.push({ label: `It's only ${login}'s turn`, run: () => void pr.changeAttention(onlyChanges(login, pr.attention)) });
    if (overrideOf(login) && pr.canSetAttention(login))
      out.push({ label: 'Undo manual change', run: () => void pr.changeAttention([{ login, state: null }]) });
    return out;
  }

  /** People the picker offers: participants plus current members, filtered by what the mode allows. */
  const candidates = $derived.by((): Participant[] => {
    if (!open || open.kind === 'member') return [];
    const list = [...pr.participants];
    for (const m of pr.attention) if (!list.some((p) => sameLogin(p.login, m.login))) list.push({ login: m.login, roles: [] });
    if (me && !list.some((p) => sameLogin(p.login, me))) list.push({ login: me, roles: ['you'] });
    const shown = open.kind === 'add' ? list.filter((p) => !inSet(p.login) && pr.canSetAttention(p.login)) : list;
    return filterParticipants(shown, query);
  });

  // Attention changes are server data: none while it is off (see PrStore.serverData).
  const canAdd = $derived(pr.serverData && (pr.canSetOthersAttention || (!!me && !inSet(me))));
  const canTurn = $derived(pr.canSetOthersAttention);

  async function show(next: Open, el: HTMLElement) {
    if (open && trigger === el) return close();
    open = next;
    trigger = el;
    query = '';
    await tick();
    place();
    if (next.kind === 'member') pop?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    else pop?.querySelector<HTMLInputElement>('input')?.focus();
  }

  function close(refocus = true) {
    if (!open) return;
    open = null;
    if (refocus) trigger?.focus();
  }

  /** Below the trigger, kept inside the viewport (16px margin). */
  function place() {
    if (!trigger || !pop) return;
    const r = trigger.getBoundingClientRect();
    const w = pop.offsetWidth;
    const h = pop.offsetHeight;
    const left = Math.max(16, Math.min(r.left, innerWidth - w - 16));
    const below = r.bottom + 6;
    const top = below + h > innerHeight - 8 && r.top - h - 6 > 8 ? r.top - h - 6 : below;
    pos = { top, left };
  }

  function choose(login: string) {
    if (!open || open.kind === 'member') return;
    const changes = open.kind === 'add' ? addChanges(login) : onlyChanges(login, pr.attention);
    close();
    void pr.changeAttention(changes);
  }

  function menuKeydown(e: KeyboardEvent) {
    const items = [...(pop?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => items[(i + items.length) % items.length]?.focus();
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
    else if (e.key === 'Escape') close();
    else if (e.key === 'Tab') close(false);
    else return;
    e.preventDefault();
  }

  function pickerKeydown(e: KeyboardEvent) {
    const opts = [...(pop?.querySelectorAll<HTMLElement>('.attn-option') ?? [])];
    const at = opts.indexOf(document.activeElement as HTMLElement);
    const input = pop?.querySelector<HTMLInputElement>('input');
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      opts[Math.min(at + 1, opts.length - 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (at <= 0) input?.focus();
      else opts[at - 1]?.focus();
    } else if (e.key === 'Enter' && e.target === input && candidates[0]) {
      e.preventDefault();
      choose(candidates[0].login);
    }
  }

  // Follow the trigger while any container scrolls.
  $effect(() => {
    if (!open) return;
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  });

  function outside(e: PointerEvent) {
    if (!open) return;
    const t = e.target as Node;
    if (pop?.contains(t) || trigger?.contains(t)) return;
    close(false);
  }
</script>

<svelte:window onpointerdown={outside} onresize={() => close(false)} />

{#snippet member(m: (typeof pr.attention)[number])}
  {@const acts = actions(m.login)}
  {@const why = reasonText(m.reason)}
  {@const isOpen = open?.kind === 'member' && open.login === m.login}
  {#if acts.length}
    <button
      class="attn-who"
      class:me={sameLogin(m.login, me)}
      aria-haspopup="menu"
      aria-expanded={isOpen}
      aria-controls={isOpen ? `attn-menu-${uid}` : undefined}
      aria-label="{m.login}, {why}: attention options"
      title={why}
      onclick={(e) => show({ kind: 'member', login: m.login }, e.currentTarget)}
    >
      <b>{m.login}</b>{#if layout === 'inline'}<span class="muted">({why})</span>{/if}<span class="caret" aria-hidden="true">▾</span>
    </button>
  {:else}
    <span class="attn-who static" class:me={sameLogin(m.login, me)} title="{why}. {NO_RIGHTS}.">
      <b>{m.login}</b>{#if layout === 'inline'}<span class="muted">({why})</span>{/if}
    </span>
  {/if}
{/snippet}

{#snippet tools()}
  {#if canAdd}
    <button
      class="attn-tool"
      aria-haspopup="dialog"
      aria-expanded={open?.kind === 'add'}
      onclick={(e) => show({ kind: 'add' }, e.currentTarget)}>+ Add</button
    >
  {/if}
  {#if canTurn}
    <button
      class="attn-tool"
      aria-haspopup="dialog"
      aria-expanded={open?.kind === 'turn'}
      title="Make one person the only one waiting"
      onclick={(e) => show({ kind: 'turn' }, e.currentTarget)}>Set turn…</button
    >
  {/if}
{/snippet}

{#if layout === 'inline'}
  {#if pr.attention.length === 0}
    <span class="muted">nobody</span>
  {:else}
    {#each pr.attention as m, i (m.login)}
      {#if i > 0}<span class="sep" aria-hidden="true">·</span>{/if}
      {@render member(m)}
    {/each}
    {#if longest}
      <span class="attn-for muted" title={sinceTitle(longestSince, longest.bound)}>{longestText}</span>
    {/if}
  {/if}
  {@render tools()}
  {#if saving}<PendingTag label={saving.label} />{/if}
{:else}
  <ul class="people" class:is-pending={!!saving} aria-busy={saving ? 'true' : undefined}>
    {#each pr.attention as m (m.login)}
      <li>
        {@render member(m)}
        <span class="muted">{reasonText(m.reason)}</span>
        {#if waitText(m.since, now, 'long')}<span class="attn-wait" title={sinceTitle(m.since, m.sinceBound)}
            >waiting {waitText(m.since, now, 'long', m.sinceBound)}{#if m.sinceBound}<span class="sr-only"> or more</span>{/if}</span
          >{/if}
      </li>
    {:else}
      <li class="muted">Nobody</li>
    {/each}
  </ul>
  {#if canAdd || canTurn || saving}
    <div class="attn-tools">
      {#if canAdd || canTurn}{@render tools()}{/if}
      {#if saving}<PendingTag label={saving.label} />{/if}
    </div>
  {/if}
{/if}

{#if open}
  <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
  <div
    bind:this={pop}
    class="attn-pop"
    style:top="{pos.top}px"
    style:left="{pos.left}px"
    role={open.kind === 'member' ? 'menu' : 'dialog'}
    id={open.kind === 'member' ? `attn-menu-${uid}` : undefined}
    aria-label={open.kind === 'member' ? `Attention: ${open.login}` : open.kind === 'add' ? 'Add to the attention set' : 'Set whose turn it is'}
    tabindex="-1"
    onkeydown={open.kind === 'member' ? menuKeydown : pickerKeydown}
  >
    {#if open.kind === 'member'}
      {#each actions(open.login) as a}
        <button role="menuitem" tabindex="-1" class="attn-item" onclick={() => { close(); a.run(); }}>{a.label}</button>
      {/each}
    {:else}
      <p class="attn-pop-head">{open.kind === 'add' ? 'Add to the attention set' : 'Only this person is waiting'}</p>
      <input
        type="search"
        placeholder="Search participants"
        aria-label="Search participants"
        aria-controls="attn-list-{uid}"
        bind:value={query}
        autocomplete="off"
        spellcheck="false"
      />
      <ul class="attn-list" id="attn-list-{uid}">
        {#each candidates as p (p.login)}
          <li>
            <button class="attn-option" onclick={() => choose(p.login)}>
              <b>{p.login}</b>{#if inSet(p.login)}<span class="pill pending">waiting</span>{/if}
              <span class="muted">{p.roles.join(', ')}</span>
            </button>
          </li>
        {:else}
          <li class="muted attn-empty">{query ? 'No participant matches' : 'Everyone is already waiting'}</li>
        {/each}
      </ul>
    {/if}
  </div>
{/if}

<style>
  /* Under the reason, so a long reason keeps its width. */
  .people li > .attn-wait {
    grid-column: 2;
    justify-self: start;
    margin-top: -6px;
    white-space: nowrap;
    font-size: 12px;
    color: var(--muted);
    font-variant-numeric: tabular-nums;
  }
  .attn-for {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  .attn-who {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 1px solid transparent;
    background: none;
    color: inherit;
    padding: 1px 5px;
    margin: -2px -1px;
    border-radius: 6px;
    font-size: inherit;
    line-height: inherit;
  }
  .attn-who b {
    font-weight: 700;
  }
  .attn-who.me b {
    color: var(--accent);
  }
  button.attn-who:hover {
    border-color: var(--line);
    background: var(--code);
  }
  button.attn-who:focus-visible,
  .attn-tool:focus-visible,
  .attn-item:focus-visible,
  .attn-option:focus-visible {
    outline-offset: 1px;
  }
  button.attn-who[aria-expanded='true'] {
    border-color: var(--accent);
    background: var(--soft);
  }
  .caret {
    color: var(--muted);
    font-size: 10px;
  }
  .attn-tool {
    border: 1px dashed var(--line);
    background: none;
    color: var(--muted);
    border-radius: 20px;
    padding: 1px 9px;
    font-size: 12px;
  }
  .attn-tool:hover,
  .attn-tool[aria-expanded='true'] {
    color: var(--accent);
    border-color: var(--accent);
  }
  .attn-tools {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 10px;
  }
  .attn-pop {
    position: fixed;
    z-index: 45;
    display: grid;
    gap: 2px;
    padding: 6px;
    width: max-content;
    min-width: 200px;
    max-width: min(300px, calc(100vw - 32px));
    border-radius: 9px;
    background: var(--surface);
    color: var(--fg);
    border: 1px solid var(--line);
    box-shadow: var(--shadow), 0 4px 16px #0002;
    font-size: 13px;
    text-transform: none;
    letter-spacing: 0;
  }
  .attn-pop-head {
    color: var(--muted);
    font-size: 11px;
    padding: 2px 6px 4px;
  }
  .attn-pop input {
    border: 1px solid var(--line);
    background: var(--bg);
    color: var(--fg);
    border-radius: 6px;
    padding: 5px 8px;
    font-size: 13px;
    margin-bottom: 4px;
    min-width: 0;
  }
  .attn-item,
  .attn-option {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    text-align: left;
    border: 0;
    background: none;
    color: inherit;
    padding: 6px 8px;
    border-radius: 6px;
    font-size: 13px;
  }
  .attn-item:hover,
  .attn-option:hover,
  .attn-item:focus,
  .attn-option:focus {
    background: var(--soft);
  }
  .attn-option .muted {
    margin-left: auto;
    font-size: 11px;
    padding-left: 8px;
  }
  .attn-option .pill {
    font-size: 10px;
    padding: 1px 6px;
  }
  .attn-list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 240px;
    overflow: auto;
  }
  .attn-empty {
    padding: 6px 8px;
    font-size: 12px;
  }
</style>
